import {
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { ArrowUp, Paperclip, X } from 'lucide-react';
import { IconButton, Textarea, useToast } from '@/components/ui';
import {
  MicButton,
  appendDictated,
  useDictation,
  type TranscriptionSource,
} from '@/components/dictation';
import { cn } from '@/lib/utils';
import {
  ACCEPTED_IMAGE_TYPES,
  imageDataUrl,
  readImageAttachment,
  type ChatImage,
} from './image-attachment';
import './Composer.css';

export interface ComposerProps {
  /** Send a (trimmed, non-empty) question, with the attached image if there is one. */
  onSend: (question: string, image?: ChatImage) => void;
  /** A question is in flight: the send button spins and a second send is blocked. */
  pending?: boolean;
  /** Accessible name of the question field. */
  inputLabel: string;
  placeholder: string;
  /** Accessible name of the send button. */
  sendLabel: string;
  /**
   * O padrão de todo chat é `enter`: Enter envia e Shift+Enter quebra a linha.
   * `mod-enter` fica apenas para uma superfície que explicitamente precise do
   * comportamento legado, Ctrl/Cmd+Enter.
   */
  sendOn?: 'enter' | 'mod-enter';
  /** Opens the footer row, on the left (the Chat's question options). */
  options?: ReactNode;
  /** Lets the owner hand the cursor back to the field (e.g. after "Nova conversa"). */
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** Tighter box for a narrow surface (the floating widget). */
  compact?: boolean;
  /** Image attach (picker and paste). Off where the backend reads no image (the public site). */
  attach?: boolean;
  /** Where the microphone clip becomes text; the console's transcription when absent. */
  transcription?: TranscriptionSource;
  /** The backend's ceiling for the field, where there is one. */
  maxLength?: number;
}

/**
 * The question composer shared by the Knowledge chat, the Shiba widget and the
 * public site widget: one rounded surface holding the field, with attach,
 * microphone and send INSIDE its border on a footer row. The field grows with
 * the text up to a ceiling. Up to 720px (Composer.css) the surface is a
 * one-line pill instead, as in the Gemini app: options, field, attach,
 * microphone and send in one row, with the chip and the hint above it.
 *
 * An image can ride along, chosen from the picker or simply pasted (a print
 * screen is the common case). It is validated here — format and size — so a file
 * the vision model would refuse never costs a round trip, and it lives as a chip
 * above the field until it is sent or removed.
 */
export function Composer({
  onSend,
  pending = false,
  inputLabel,
  placeholder,
  sendLabel,
  sendOn = 'enter',
  options,
  inputRef: externalRef,
  compact = false,
  attach = true,
  transcription,
  maxLength,
}: ComposerProps): JSX.Element {
  const [text, setText] = useState('');
  const [image, setImage] = useState<ChatImage | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const ownRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = externalRef ?? ownRef;
  const hintId = useId();
  const { toast } = useToast();
  const dictation = useDictation((dictated) => {
    setText((current) => appendDictated(current, dictated));
    inputRef.current?.focus();
  }, transcription);

  const attachImage = async (file: File): Promise<void> => {
    const result = await readImageAttachment(file);
    if (!result.ok) {
      toast({ tone: 'error', title: 'Imagem não anexada', description: result.reason });
      return;
    }
    setImage(result.image);
  };

  const submit = (): void => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    onSend(trimmed, image ?? undefined);
    setText('');
    setImage(null);
    // O envio desabilita o botão no MESMO commit: sem devolver o foco aqui ele cai
    // no body e o teclado perde o compositor.
    inputRef.current?.focus();
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    submit();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter') return;
    const sends =
      sendOn === 'enter' ? !event.shiftKey : event.metaKey || event.ctrlKey;
    if (!sends) return;
    event.preventDefault();
    submit();
  };

  // Colar é o caminho natural de um print de tela: o arquivo vem na área de
  // transferência sem nunca passar por uma janela de "abrir arquivo".
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    const file = Array.from(event.clipboardData?.files ?? [])[0];
    if (!attach || !file) return;
    event.preventDefault();
    void attachImage(file);
  };

  const missingText = image !== null && !text.trim();

  return (
    <form className={cn('composer', compact && 'composer--compact')} onSubmit={onSubmit}>
      {image && (
        <div className="composer__attachment">
          <img className="composer__attachment-thumb" src={imageDataUrl(image)} alt="" />
          <span className="composer__attachment-name">{image.name}</span>
          <IconButton
            size="sm"
            label={`Remover imagem ${image.name}`}
            icon={<X />}
            onClick={() => setImage(null)}
          />
        </div>
      )}
      {/* A pergunta é obrigatória mesmo com anexo (o backend exige `question`),
          e um botão de enviar morto sem explicação é um beco: a dica diz por
          que ele está desabilitado, e o aria-describedby leva a mesma razão a
          quem navega por leitor de tela. */}
      {missingText && (
        <p id={hintId} className="composer__hint">
          Descreva o que você quer saber sobre a imagem para enviar.
        </p>
      )}
      {/* O campo e os botões, sem o chip e a dica: no celular é esta caixa que
          vira a pílula de uma linha, e o chip e a dica ficam acima dela. */}
      <div className="composer__box">
        <Textarea
          ref={inputRef}
          aria-label={inputLabel}
          rows={1}
          maxLength={maxLength}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="composer__input"
        />
        <div className="composer__row">
          {attach && (
            <>
              <input
                ref={fileRef}
                type="file"
                className="composer__file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                aria-label="Anexar imagem à pergunta"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // O valor é limpo para que escolher O MESMO arquivo de novo (depois
                  // de remover o chip) volte a disparar o change.
                  e.target.value = '';
                  if (file) void attachImage(file);
                }}
              />
              <button
                type="button"
                className="composer__attach"
                aria-label="Anexar imagem"
                disabled={pending}
                onClick={() => fileRef.current?.click()}
              >
                <Paperclip size={16} aria-hidden="true" />
              </button>
            </>
          )}
          {options && <div className="composer__options">{options}</div>}
          <MicButton dictation={dictation} size="lg" disabled={pending} className="composer__mic" />
          <IconButton
            type="submit"
            variant="primary"
            size="lg"
            label={sendLabel}
            icon={<ArrowUp />}
            aria-describedby={missingText ? hintId : undefined}
            loading={pending}
            disabled={!text.trim()}
            className="composer__send"
          />
        </div>
      </div>
    </form>
  );
}
